import React, { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useHistory, useParams } from 'react-router-dom';
import I18n from 'i18n-js';

import useCollectionState from '@lootboy/lib/slices/cardCollections/hooks/useCollectionState';
import useFetchedCollectionData from '@lootboy/lib/slices/cardCollections/hooks/useFetchedCollectionData';
import { isFetched } from '@lootboy/lib/slices/cardCollections/types';
import { collectionProgressFetchErrors } from '@lootboy/lib/slices/cardCollections/utils';

import ActivityIndicator from '~/components/ActivityIndicator';
import Alert from '~/components/Alert';
import ButtonClean from '~/components/ButtonClean';
import Image from '~/components/Image';
import { ANIMATED_ROOT_POPUP_ID } from '~/components/molecules/BottomSheetDialog/utils';
import { CardPopupContainer } from '~/components/molecules/CardPopupContainer';

import { useClosePopup } from '~/hooks/useClosePopup';
import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import { CARDS_PATH } from '~/routes/paths';
import CollectionStatusPill from '~/views/CollectionView/CollectionStatusPill';
import FetchedCollectionContent from '~/views/CollectionView/FetchedCollectionContent';

import { ReactComponent as CloseIcon } from '~/assets/images/icons/i-close.svg';

import styles from './CollectionView.module.scss';

const CollectionView: React.FC = () => {
  const closePopup = useClosePopup();
  const history = useHistory();
  const alert = useAlert();
  const { alias } = useParams<{ alias: string }>();

  const onCollectionFetchFail = useCallback(
    (e) => {
      const { messageId } = e as { messageId?: string };
      if (messageId === collectionProgressFetchErrors.notAvailableForRegion) {
        alert.error(<Alert title={I18n.t('collections_pu_error_regional')} />);
        history.replace(CARDS_PATH);
      } else if (messageId === collectionProgressFetchErrors.notFound) {
        history.replace(CARDS_PATH);
      }
    },
    [alert, history],
  );

  const { collection, loading } = useFetchedCollectionData(
    alias,
    onCollectionFetchFail,
  );

  useDocumentTitle(collection?.headline ?? '');

  const collectionState = useCollectionState(collection?._id || '');

  const isFetching =
    loading ||
    !collection ||
    !isFetched(collection) ||
    collectionState === null;

  return (
    <CardPopupContainer
      onClose={closePopup}
      className={styles.container}
      contentElementId={ANIMATED_ROOT_POPUP_ID}
      key={alias}
    >
      <ButtonClean
        className={styles.closeButton}
        onClick={closePopup}
        aria-label={I18n.t('common_back')}
      >
        <CloseIcon />
      </ButtonClean>
      <div className={styles.collectionContent}>
        {collection && (
          <Image
            path={collection.headerImage}
            alt="Collection header image"
            className={styles.headerImage}
          />
        )}
        <section className={styles.headerSection}>
          <h2>{collection?.headline}</h2>
          <div>
            <h3>{collection?.subHeadline}</h3>
            {!isFetching && (
              <CollectionStatusPill
                collectionState={collectionState}
                availableUntil={collection.availableUntil}
              />
            )}
          </div>
        </section>
        {!isFetching ? (
          <FetchedCollectionContent
            collection={collection}
            collectionState={collectionState}
          />
        ) : (
          <div className={styles.space}>
            <ActivityIndicator />
          </div>
        )}
      </div>
    </CardPopupContainer>
  );
};

export default CollectionView;
